import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { Link } from "./Link";
import { LinkProps } from "./types";
import { getImageSizes } from "./utils";

export interface CardCityProps extends Omit<ComponentProps<"div">, "children"> {
  image?: {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
  } | null;
  title?: string;
  link?: LinkProps | null;
  aspectRatio?: "square" | "tall";
  sizes?: string;
  onClick?: () => void;
}

export const CardCity: React.FC<CardCityProps> = ({
  image,
  title,
  link,
  aspectRatio = "tall",
  sizes,
  onClick,
  className,
  ...props
}) => {
  const imageSizes = sizes || getImageSizes(image?.src);
  const cardClasses = cn(
    "uipm:relative uipm:overflow-hidden uipm:rounded-[var(--radius-lg)]",
    "uipm:flex uipm:flex-col uipm:justify-end",
    "uipm:min-h-[300px]",
    aspectRatio === "square" ? "uipm:aspect-[1/1]" : "uipm:aspect-[291/416]",
    link &&
      "uipm:cursor-pointer uipm:transition-transform hover:uipm:scale-[1.02] uipm:no-underline",
    className,
  );

  const cardContent = (
    <>
      {image && (
        <>
          <Image
            src={image.src}
            alt={parseAiAlt(image.alt).cleanAlt}
            fill
            sizes={imageSizes}
            className="uipm:object-cover uipm:rounded-[var(--radius-sm)]"
          />
          <AiDisclaimerForAlt alt={image.alt} src={image.src} />
        </>
      )}

      {/* Content Section */}
      <div
        className={cn(
          "uipm:flex uipm:flex-col",
          "uipm:pt-[var(--container-section-bottom)]",
          "uipm:px-[var(--padding-padding-sm-plus)]",
          "uipm:pb-[var(--container-card-overlay-padding)]",
          "uipm:bg-[linear-gradient(180deg,rgba(34,68,47,0)0%,rgba(34,68,47,0.8)59.62%)]",
          "uipm:z-1",
        )}
      >
        {title && (
          <h3
            className={cn(
              "uipm:font-[var(--font-family-sans)]",
              "uipm:font-semibold",
              "uipm:text-[var(--typography-colors-text-inverted)]",
              "uipm:text-(length:--typography-sizes-headline-4)",
              "uipm:leading-[var(--typography-line-heights-headline-4)]",
              "uipm:tracking-(--typography-letter-spacing-headline-4)",
            )}
          >
            {title}
          </h3>
        )}
      </div>
    </>
  );

  const handleClick = () => {
    onClick?.();
  };

  if (link) {
    return (
      <Link
        href={link.href}
        target={link.target}
        noFollow={link.noFollow}
        className={cardClasses}
        onClick={handleClick}
        {...(props as ComponentProps<"a">)}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div className={cardClasses} onClick={handleClick} {...props}>
      {cardContent}
    </div>
  );
};
